// ============================================================================
// EnterpriseScreenShell — opinionated wrapper that composes ModuleHeader +
// MinimizedModalStrip + standard confirm portal + module-content wrapper
// ============================================================================
// Use this when a screen wants the enterprise defaults without hand-wiring
// every piece (ModuleHeader, MinimizedModalStrip, ConfirmDialogPortal, the
// .module-master-container / .module-content / .data-grid-container layout
// classes). Screens that need bespoke layouts can still use the underlying
// pieces directly — this is a convenience, not a mandate.
//
// MINIMAL EXAMPLE (a list screen):
//
//   const actionButtons = [
//     actionButtonFactory.refresh(refetch),
//     actionButtonFactory.add(handleAdd),
//   ];
//
//   const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();
//
//   const handleDelete = async (record) => {
//     const ok = await confirmDelete({ resourceLabel: record.name });
//     if (ok) await service.delete(record.id);
//   };
//
//   return (
//     <EnterpriseScreenShell
//       title="My Module Master"
//       description="Manage things"
//       icon={<IconSystem.folder size={18} />}
//       tabId={tabId}
//       actionButtons={actionButtons}
//       ConfirmDialogPortal={ConfirmDialogPortal}
//       filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
//       // FILTER PANEL GOES IN `trailingContent` — NOT in children.
//       // `.module-content` is a flex ROW: children occupy the left column and
//       // this slot is its sibling to the RIGHT. A `.search-panel` placed inside
//       // children instead becomes a row in the left column, renders where the
//       // grid should be, and collapses the grid to nothing.
//       trailingContent={
//         <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
//           <div className="search-panel-header">
//             <h3 className="search-panel-title">Filters</h3>
//             <button className="search-panel-close" onClick={() => setFilterOpen(false)}>
//               <IconSystem.x size={14} />
//             </button>
//           </div>
//           <div className="search-panel-content">{/* filter fields */}</div>
//         </div>
//       }
//     >
//       <div className="data-grid-container">
//         <SyutGrid {...gridProps} />
//       </div>
//     </EnterpriseScreenShell>
//   );

import React, { type ReactNode } from 'react';
import ModuleHeader from '../modals/common/ModuleHeader';
import type { ActionButton, ViewModeOption } from '../modals/common/ModuleHeader';
import { MinimizedModalStrip } from '../ui';

interface SearchConfig {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

interface ViewModeConfig {
  currentMode: string;
  modes: ViewModeOption[];
  onChange: (m: string) => void;
}

interface FilterConfig {
  onClick: () => void;
  isActive?: boolean;
  appliedCount?: number;
  disabled?: boolean;
  tooltip?: string;
}

interface EnterpriseScreenShellProps {
  /** Module title rendered in the header (e.g. "Sales Order Master"). */
  title: string;
  /** One-line description rendered under the title. */
  description?: string;
  /** Header icon. Use `<IconSystem.xxx size={18} />`. */
  icon?: ReactNode;
  /**
   * The tab id this screen is mounted in. When provided, the header
   * automatically renders `<MinimizedModalStrip tabId={tabId} />` as the
   * leadingToolbarItems — no need to wire it in the screen.
   */
  tabId?: string;
  /** Action buttons cluster on the right of the header. Use the factory. */
  actionButtons?: ActionButton[];
  /** Toolbar search bar config (optional). */
  searchConfig?: SearchConfig;
  /** Toolbar view-mode toggle (list/grid/calendar). */
  viewModeConfig?: ViewModeConfig;
  /** Toolbar filter button + applied-count badge. */
  filterConfig?: FilterConfig;
  /** Extra toolbar slot rendered just before `actions` (e.g. ColumnSelector). */
  customToolbarItems?: ReactNode;
  /**
   * Render the confirm-dialog portal from useStandardConfirms here.
   * Required ONLY if you want the shell to render it; you can render it
   * manually at the bottom of the screen if you prefer.
   */
  ConfirmDialogPortal?: React.FC;
  /**
   * Main screen content. Wrap grids in `<div className="data-grid-container">`.
   *
   * The filter panel does NOT go here — see `trailingContent`.
   */
  children: ReactNode;
  /**
   * Slot rendered BESIDE `children`, to the RIGHT. **This is where the filter
   * panel goes.**
   *
   * `.module-content` is `display: flex; flex-direction: row`. `children` are
   * wrapped in the left-hand column and this slot is its flex sibling, so a
   * `.search-panel` placed here slides in at the right edge as intended.
   *
   * Putting the panel inside `children` instead is the single most common way to
   * break a shell-based screen: it becomes a row in the left column, renders
   * left-aligned where the grid should be, collapses the grid rows to nothing,
   * and pushes its own header out of view. This JSDoc previously read "slot
   * below children", and every screen written against that description shipped
   * the bug — 4 of 4 in the first adopting app.
   *
   * @example
   * <EnterpriseScreenShell
   *   title="Customers"
   *   filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
   *   trailingContent={
   *     <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
   *       ...
   *     </div>
   *   }
   * >
   *   <div className="data-grid-container">
   *     <SyutGrid ... />
   *   </div>
   * </EnterpriseScreenShell>
   */
  trailingContent?: ReactNode;
}

export const EnterpriseScreenShell: React.FC<EnterpriseScreenShellProps> = ({
  title,
  description,
  icon,
  tabId,
  actionButtons,
  searchConfig,
  viewModeConfig,
  filterConfig,
  customToolbarItems,
  ConfirmDialogPortal,
  children,
  trailingContent,
}) => {
  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title={title}
        description={description}
        icon={icon}
        searchConfig={searchConfig}
        viewModeConfig={viewModeConfig}
        filterConfig={filterConfig}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={customToolbarItems}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        {/* `children` is wrapped in a VERTICAL flex column so screens can pass
            multiple sections (context bar + empty-state pill + grid container)
            without each screen needing to remember the wrapper boilerplate.
            `.module-content` itself is `display: flex; flex-direction: row`
            and `trailingContent` (the filter panel) sits to the right of this
            column as a flex sibling. SampleMasterScreen, UserMaster, etc. all
            pass a single grid container as children and still work — adding
            more sections is now safe by default. */}
        <div style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          overflow: 'hidden',
        }}>
          {children}
        </div>
        {trailingContent}
      </div>

      {ConfirmDialogPortal && <ConfirmDialogPortal />}
    </div>
  );
};

export default EnterpriseScreenShell;
